// Attributes, effects and abilities on the Gameplay Ability System model (decision 0050; the notes' "what we took"
// paragraph: engine/docs/spikes/002-green-valley/notes/b-combat.md).
//
// - An attribute has a base value and a current value. Instant effects (and each tick of a periodic one) change the
//   base; timed and infinite effects change only the current value while they are active. The current value is the
//   base with the active modifiers applied in a fixed order: every add, then every multiply, then the last override.
//   Both are clamped to the attribute's min and max, which may name another attribute (hp ≤ hpMax).
// - An effect is instant, timed or infinite, optionally periodic; it may grant tags while active, require tags on its
//   target, remove effects that grant given tags, and stack (by target or by source, up to a limit, with a duration
//   policy and an expiry policy). Stacks scale its modifiers: add × stacks, multiply ^ stacks.
// - An ability has a cost (attribute amounts it spends), a cooldown (a timed effect granting a cooldown tag), tag
//   requirements on its owner, and tags of its own.
//
// Everything a character has is one plain `AbilityState` (JSON-safe: the kernel saves and hashes it); the set of
// definitions is the game's data, passed in. No clock and no randomness here: the game steps it with its fixed dt.
// Phase D1 builds on these seams: experience and levels and skill trees as infinite effects reconciled by `sync`
// (progression.ts); a class template is `init`'s overrides plus its starting effects (classes.ts).
import { hasTag, matchTags, type Tag, type TagQuery } from "./tags";

/** One attribute the game defines (a stat set is a list of these). */
export interface AttributeDef {
  id: string;
  /** The starting base value. */
  base: number;
  /** Lower and upper clamps: a number, or another attribute's id (its current value). */
  min?: number | string;
  max?: number | string;
}

/** How a modifier changes a value. Applied in this order: add, multiply, override. */
export type ModOp = "add" | "multiply" | "override";

/** One modifier: an attribute, an operation and a value. */
export interface Modifier {
  attribute: string;
  op: ModOp;
  value: number;
}

/** How repeat applications of one effect combine. */
export interface StackingRule {
  /** The most stacks (≥ 1). */
  limit: number;
  /** "target": one stack per target whatever the source; "source": one stack per source. Default "target". */
  by?: "target" | "source";
  /** On a new stack: "refresh" the duration (default), "keep" it, or "add" the effect's seconds to it. */
  duration?: "refresh" | "keep" | "add";
  /** When the duration runs out: remove "all" stacks (default), or "one" and refresh the duration. */
  expire?: "all" | "one";
}

/** An effect the game defines. */
export interface EffectDef {
  id: string;
  /** "instant" changes base values once; "timed" lasts `seconds`; "infinite" lasts until removed. */
  duration: "instant" | "timed" | "infinite";
  seconds?: number;
  /** Periodic (timed or infinite only): every `period` seconds its modifiers change the base, like an instant. */
  period?: number;
  /** A periodic effect also ticks once when applied. */
  tickOnApply?: boolean;
  modifiers?: readonly Modifier[];
  /** Tags the target has while the effect is active. */
  grants?: readonly Tag[];
  /** Applied only when the target's tags match. */
  requires?: TagQuery;
  /** On apply, removes the target's active effects that grant any of these tags (a cleanse). */
  removes?: readonly Tag[];
  stacking?: StackingRule;
}

/** An ability the game defines. */
export interface AbilityDef {
  id: string;
  /** The ability's own tags ("ability.attack.light"). */
  tags?: readonly Tag[];
  /** What activating it spends. It can't activate when any amount is more than the attribute's current value. */
  cost?: readonly { attribute: string; amount: number }[];
  /** Seconds before it can activate again (a timed effect granting `cooldownTag`). */
  cooldown?: number;
  /** The tag the cooldown grants. Default `cooldown.<id>`. */
  cooldownTag?: Tag;
  /** The owner's tags must match (e.g. `{ none: ["state.stunned"] }`). */
  requires?: TagQuery;
}

/** One active (timed or infinite) effect on a character. */
export interface ActiveEffect {
  /** Unique within its state, in application order. */
  uid: number;
  id: string;
  /** Who applied it (an entity id), or null. */
  source: number | null;
  stacks: number;
  /** Seconds left, or null for infinite. */
  left: number | null;
  /** Seconds to the next periodic tick, or null when not periodic. */
  nextTick: number | null;
}

/** A character's whole ability state. Plain JSON: save it, hash it, restore it. */
export interface AbilityState {
  /** Base values by attribute id. */
  base: Record<string, number>;
  effects: ActiveEffect[];
  /** Loose tags (set by the game directly, counted). */
  loose: Record<Tag, number>;
  /** The next effect uid. */
  uid: number;
}

/** Why an effect didn't apply or an ability didn't activate. */
export type Refusal = "unknown" | "requirements" | "cost" | "cooldown";

/** What `step` reports. */
export interface EffectEvent {
  kind: "tick" | "expired" | "stackLost";
  id: string;
  uid: number;
}

/** Planted defects beside what they break (tests only). */
export const ABILITY_FAULTS = {
  /** Multiply modifiers apply before add modifiers: the modifier-order test must go red. */
  multiplyBeforeAdd: false,
};

/** The game's definitions. */
export interface AbilityDefs {
  attributes: readonly AttributeDef[];
  effects?: readonly EffectDef[];
  abilities?: readonly AbilityDef[];
}

/** The ability system over one set of definitions. Every method takes the character's state. */
export interface AbilitySet {
  readonly attributes: ReadonlyMap<string, AttributeDef>;
  /** A fresh state: each attribute at its base (or `overrides`' value: a class template's starting stats). */
  init(overrides?: Record<string, number>): AbilityState;
  /** The current value: the base with active modifiers (add, multiply, override), clamped. */
  value(s: AbilityState, attribute: string): number;
  base(s: AbilityState, attribute: string): number;
  /** Set a base value (clamped): levels and growth write here. */
  setBase(s: AbilityState, attribute: string, value: number): void;
  /** Every tag the character has now (granted by effects and loose), each once, sorted. */
  tags(s: AbilityState): Tag[];
  hasTag(s: AbilityState, tag: Tag, exact?: boolean): boolean;
  matches(s: AbilityState, q: TagQuery): boolean;
  /** Add or remove a loose tag (counted: two adds need two removes). */
  addTag(s: AbilityState, tag: Tag): void;
  removeTag(s: AbilityState, tag: Tag): void;
  /** Apply an effect (by id or definition). Returns the active effect's uid (0 for an instant), or a refusal. */
  apply(s: AbilityState, effect: string | EffectDef, o?: { source?: number | null }): { uid: number } | { refused: Refusal };
  /** Remove one active effect by uid, or every active effect with this id. */
  remove(s: AbilityState, uid: number): void;
  removeById(s: AbilityState, id: string): void;
  /** Age every active effect by `dt` seconds: periodic ticks, expiry, stacks lost. */
  step(s: AbilityState, dt: number): EffectEvent[];
  /** Null when the ability may activate now, else why not. */
  canActivate(s: AbilityState, ability: string): Refusal | null;
  /** Activate: pay the cost and start the cooldown. Null on success, else why not (nothing spent). */
  activate(s: AbilityState, ability: string): Refusal | null;
  /** Seconds left on an ability's cooldown (0 when ready). */
  cooldownLeft(s: AbilityState, ability: string): number;
  /** How long the character keeps a tag: the most seconds left on a timed effect granting it (hierarchically), Infinity
   *  when an infinite effect or a loose tag grants it, 0 when nothing does (a stun's time left, a HUD's status timer). */
  tagTime(s: AbilityState, tag: Tag): number;
  /**
   * Make the character carry exactly the effects one owner wants (a level, a skill tree's bought nodes, the equipped
   * charms): each wanted definition is applied when missing, set to its stack count, and every active effect whose id
   * starts with `${owner}:` that is not wanted is removed. Wanted ids must start with `${owner}:`. Idempotent: a game
   * calls it every step (or after a change), so a fighter re-created from its base values gets them back.
   */
  sync(s: AbilityState, owner: string, wanted: readonly { def: EffectDef; stacks?: number }[]): void;
}

/** Build the ability system over the game's definitions. Throws on a duplicate id or a modifier naming no attribute. */
export function createAbilities(defs: AbilityDefs): AbilitySet {
  const attributes = new Map<string, AttributeDef>();
  for (const a of defs.attributes) {
    if (attributes.has(a.id)) throw new Error(`abilities: attribute "${a.id}" defined twice`);
    attributes.set(a.id, a);
  }
  const effects = new Map<string, EffectDef>();
  const abilities = new Map<string, AbilityDef>();
  const checkMods = (e: EffectDef): void => {
    for (const m of e.modifiers ?? []) if (!attributes.has(m.attribute)) throw new Error(`abilities: effect "${e.id}" modifies unknown attribute "${m.attribute}"`);
    if (e.duration === "timed" && !(Number(e.seconds) > 0)) throw new Error(`abilities: timed effect "${e.id}" needs seconds > 0`);
    if (e.period !== undefined && (e.duration === "instant" || !(e.period > 0))) throw new Error(`abilities: effect "${e.id}": a period needs a timed or infinite effect and period > 0`);
  };
  for (const e of defs.effects ?? []) {
    if (effects.has(e.id)) throw new Error(`abilities: effect "${e.id}" defined twice`);
    checkMods(e);
    effects.set(e.id, e);
  }
  const cooldownId = (a: string): string => `cooldown:${a}`;
  for (const a of defs.abilities ?? []) {
    if (abilities.has(a.id)) throw new Error(`abilities: ability "${a.id}" defined twice`);
    for (const c of a.cost ?? []) if (!attributes.has(c.attribute)) throw new Error(`abilities: ability "${a.id}" costs unknown attribute "${c.attribute}"`);
    abilities.set(a.id, a);
    if (a.cooldown && a.cooldown > 0) effects.set(cooldownId(a.id), { id: cooldownId(a.id), duration: "timed", seconds: a.cooldown, grants: [a.cooldownTag ?? `cooldown.${a.id}`] });
  }

  const attr = (id: string): AttributeDef => {
    const a = attributes.get(id);
    if (!a) throw new Error(`abilities: unknown attribute "${id}"`);
    return a;
  };
  const defOf = (e: ActiveEffect): EffectDef | undefined => effects.get(e.id);

  // the clamps: a number, or another attribute's current value (guarded against a cycle)
  const bound = (s: AbilityState, b: number | string | undefined, depth: number): number | undefined =>
    b === undefined ? undefined : typeof b === "number" ? b : depth > 4 ? undefined : current(s, b, depth + 1);
  const clamp = (s: AbilityState, a: AttributeDef, v: number, depth: number): number => {
    const lo = bound(s, a.min, depth);
    const hi = bound(s, a.max, depth);
    if (hi !== undefined && v > hi) v = hi;
    if (lo !== undefined && v < lo) v = lo;
    return v;
  };

  function current(s: AbilityState, id: string, depth = 0): number {
    const a = attr(id);
    let add = 0;
    let mul = 1;
    let override: number | null = null;
    for (const e of s.effects) {
      const d = defOf(e);
      if (!d || d.period !== undefined) continue; // a periodic effect changes the base on its ticks, not the current
      for (const m of d.modifiers ?? []) {
        if (m.attribute !== id) continue;
        if (m.op === "add") add += m.value * e.stacks;
        else if (m.op === "multiply") mul *= m.value ** e.stacks;
        else override = m.value;
      }
    }
    const b = s.base[id] ?? a.base;
    // the planted defect sits here, beside the order it breaks
    const v = override ?? (ABILITY_FAULTS.multiplyBeforeAdd ? b * mul + add : (b + add) * mul);
    return clamp(s, a, v, depth);
  }

  const setBase = (s: AbilityState, id: string, v: number): void => {
    s.base[id] = clamp(s, attr(id), v, 0);
  };
  const execute = (s: AbilityState, d: EffectDef, stacks: number): void => {
    for (const m of d.modifiers ?? []) {
      const b = s.base[m.attribute] ?? attr(m.attribute).base;
      const v = m.op === "add" ? b + m.value * stacks : m.op === "multiply" ? b * m.value ** stacks : m.value;
      setBase(s, m.attribute, v);
    }
  };
  // after any base change, re-clamp bases against bounds that may have moved (hpMax lowered below hp)
  const reclamp = (s: AbilityState): void => {
    for (const id of Object.keys(s.base)) s.base[id] = clamp(s, attr(id), s.base[id]!, 0);
  };

  const tagsOf = (s: AbilityState): Tag[] => {
    const set = new Set<Tag>();
    for (const [t, n] of Object.entries(s.loose)) if (n > 0) set.add(t);
    for (const e of s.effects) for (const t of defOf(e)?.grants ?? []) set.add(t);
    return [...set].sort();
  };

  const removeWhere = (s: AbilityState, pred: (e: ActiveEffect) => boolean): void => {
    s.effects = s.effects.filter((e) => !pred(e));
    reclamp(s);
  };

  const set: AbilitySet = {
    attributes,
    init(overrides = {}) {
      const s: AbilityState = { base: {}, effects: [], loose: {}, uid: 1 };
      for (const a of attributes.values()) s.base[a.id] = overrides[a.id] ?? a.base;
      for (const k of Object.keys(overrides)) attr(k);
      reclamp(s);
      return s;
    },
    value: (s, id) => current(s, id),
    base: (s, id) => s.base[id] ?? attr(id).base,
    setBase(s, id, v) {
      setBase(s, id, v);
      reclamp(s);
    },
    tags: tagsOf,
    hasTag: (s, tag, exact = false) => hasTag(tagsOf(s), tag, exact),
    matches: (s, q) => matchTags(tagsOf(s), q),
    addTag(s, tag) {
      s.loose[tag] = (s.loose[tag] ?? 0) + 1;
    },
    removeTag(s, tag) {
      const n = (s.loose[tag] ?? 0) - 1;
      if (n > 0) s.loose[tag] = n;
      else delete s.loose[tag];
    },
    apply(s, effect, o = {}) {
      const d = typeof effect === "string" ? effects.get(effect) : effect;
      if (!d) return { refused: "unknown" };
      if (typeof effect !== "string") checkMods(d);
      if (typeof effect !== "string" && !effects.has(d.id)) effects.set(d.id, d);
      if (!matchTags(tagsOf(s), d.requires)) return { refused: "requirements" };
      if (d.removes?.length) removeWhere(s, (e) => (defOf(e)?.grants ?? []).some((g) => d.removes!.some((r) => hasTag([g], r))));
      if (d.duration === "instant") {
        execute(s, d, 1);
        reclamp(s);
        return { uid: 0 };
      }
      const source = o.source ?? null;
      const seconds = d.duration === "timed" ? d.seconds! : null;
      const st = d.stacking;
      if (st) {
        const by = st.by ?? "target";
        const have = s.effects.find((e) => e.id === d.id && (by === "target" || e.source === source));
        if (have) {
          have.stacks = Math.min(Math.max(1, Math.floor(st.limit)), have.stacks + 1);
          const pol = st.duration ?? "refresh";
          if (seconds !== null && have.left !== null) have.left = pol === "refresh" ? seconds : pol === "add" ? have.left + seconds : have.left;
          reclamp(s);
          return { uid: have.uid };
        }
      }
      const e: ActiveEffect = { uid: s.uid++, id: d.id, source, stacks: 1, left: seconds, nextTick: d.period !== undefined ? d.period : null };
      s.effects.push(e);
      if (d.period !== undefined && d.tickOnApply) execute(s, d, 1);
      reclamp(s);
      return { uid: e.uid };
    },
    remove: (s, uid) => removeWhere(s, (e) => e.uid === uid),
    removeById: (s, id) => removeWhere(s, (e) => e.id === id),
    step(s, dt) {
      const out: EffectEvent[] = [];
      for (const e of [...s.effects]) {
        const d = defOf(e);
        if (!d) continue;
        // periodic ticks inside this step (a long dt can hold more than one)
        if (e.nextTick !== null && d.period) {
          const live = e.left === null ? dt : Math.min(dt, e.left);
          let next = e.nextTick - live;
          while (next <= 1e-9) {
            execute(s, d, e.stacks);
            out.push({ kind: "tick", id: e.id, uid: e.uid });
            next += d.period;
          }
          e.nextTick = next;
        }
        if (e.left === null) continue;
        e.left -= dt;
        if (e.left > 1e-9) continue;
        if (d.stacking?.expire === "one" && e.stacks > 1) {
          e.stacks--;
          e.left = d.seconds ?? 0;
          out.push({ kind: "stackLost", id: e.id, uid: e.uid });
        } else {
          s.effects.splice(s.effects.indexOf(e), 1);
          out.push({ kind: "expired", id: e.id, uid: e.uid });
        }
      }
      reclamp(s);
      return out;
    },
    canActivate(s, id) {
      const a = abilities.get(id);
      if (!a) return "unknown";
      const tags = tagsOf(s);
      if (a.cooldown && hasTag(tags, a.cooldownTag ?? `cooldown.${a.id}`, true)) return "cooldown";
      if (!matchTags(tags, a.requires)) return "requirements";
      for (const c of a.cost ?? []) if (current(s, c.attribute) < c.amount - 1e-9) return "cost";
      return null;
    },
    activate(s, id) {
      const why = set.canActivate(s, id);
      if (why) return why;
      const a = abilities.get(id)!;
      for (const c of a.cost ?? []) setBase(s, c.attribute, set.base(s, c.attribute) - c.amount);
      if (a.cooldown && a.cooldown > 0) set.apply(s, cooldownId(a.id));
      reclamp(s);
      return null;
    },
    cooldownLeft(s, id) {
      const e = s.effects.find((x) => x.id === cooldownId(id));
      return e?.left ?? 0;
    },
    tagTime(s, tag) {
      if (Object.entries(s.loose).some(([t, n]) => n > 0 && hasTag([t], tag))) return Infinity;
      let most = 0;
      for (const e of s.effects) {
        if (!(defOf(e)?.grants ?? []).some((g) => hasTag([g], tag))) continue;
        if (e.left === null) return Infinity;
        most = Math.max(most, e.left);
      }
      return most;
    },
    sync(s, owner, wanted) {
      const prefix = `${owner}:`;
      const want = new Map<string, { def: EffectDef; stacks: number }>();
      for (const w of wanted) {
        if (!w.def.id.startsWith(prefix)) throw new Error(`abilities: sync for "${owner}" given effect "${w.def.id}" (ids must start with "${prefix}")`);
        if (w.def.duration === "instant") throw new Error(`abilities: sync can't hold instant effect "${w.def.id}"`);
        want.set(w.def.id, { def: w.def, stacks: Math.max(1, Math.floor(w.stacks ?? 1)) });
      }
      let changed = false;
      const before = s.effects.length;
      s.effects = s.effects.filter((e) => !e.id.startsWith(prefix) || want.has(e.id));
      if (s.effects.length !== before) changed = true;
      for (const [id, w] of want) {
        const known = effects.get(id);
        if (known !== w.def) {
          checkMods(w.def);
          effects.set(id, w.def);
        }
        let e = s.effects.find((x) => x.id === id);
        if (!e) {
          e = { uid: s.uid++, id, source: null, stacks: 1, left: w.def.duration === "timed" ? w.def.seconds! : null, nextTick: w.def.period !== undefined ? w.def.period : null };
          s.effects.push(e);
          changed = true;
        }
        if (e.stacks !== w.stacks) {
          e.stacks = w.stacks;
          changed = true;
        }
      }
      if (changed) reclamp(s);
    },
  };
  return set;
}
